import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// ─── Palette (matches inference-results.jsx) ───
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";
const ACCENT = "#2563EB";

const fitColor = (s) => (s >= 75 ? "#22C55E" : s >= 45 ? "#F59E0B" : "#EF4444");

const Badge = ({ label, color = ACCENT }) => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 600,
      fontFamily: "'DM Mono', monospace",
      color,
      background: `${color}18`,
      border: `1px solid ${color}33`,
      borderRadius: 4,
      padding: "3px 8px",
      letterSpacing: "0.02em",
    }}
  >
    {label}
  </span>
);

const Section = ({ title, children }) => (
  <div
    style={{
      background: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: 12,
      padding: "20px 22px",
      marginBottom: 16,
    }}
  >
    <h3
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: TEXT_DIM,
        fontFamily: "'DM Mono', monospace",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        marginBottom: 14,
      }}
    >
      {title}
    </h3>
    {children}
  </div>
);

const KVRow = ({ k, v }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      padding: "8px 0",
      borderBottom: `1px solid ${BORDER}`,
      gap: 16,
    }}
  >
    <span style={{ fontSize: 12, color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{k}</span>
    <span
      style={{
        fontSize: 13,
        color: TEXT_PRIMARY,
        fontFamily: "'DM Sans', sans-serif",
        textAlign: "right",
        maxWidth: "65%",
        wordBreak: "break-word",
      }}
    >
      {v == null || v === "" ? "—" : String(v)}
    </span>
  </div>
);

const humanize = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function SharedAnalysisPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const backend = process.env.REACT_APP_BACKEND_URL;
    axios
      .get(`${backend}/api/shared/${token}`)
      .then((r) => {
        if (!cancelled) {
          setData(r.data);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? "This share link has been revoked or does not exist."
              : "We couldn't load this shared analysis."
          );
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Derived views are memoised so the read-only share page doesn't rebuild
  // every list on each render. Computed before the early returns because
  // hooks must run unconditionally.
  const view = useMemo(() => {
    const d = data || {};
    const identity = d.identity || {};
    const classification = d.classification || {};
    const meddicc = d.meddicc || {};
    return {
      identity,
      classification,
      meddicc,
      signals: Array.isArray(d.signals) ? d.signals : [],
      contacts: Array.isArray(d.key_contacts)
        ? d.key_contacts
        : Array.isArray(d.contacts)
        ? d.contacts
        : [],
      hypotheses: Array.isArray(d.hypotheses) ? d.hypotheses : [],
      articles: Array.isArray(d.articles) ? d.articles : [],
      identityRows: Object.entries(identity).filter(([k, v]) => v && k !== "confidence_score"),
      classificationRows: Object.entries(classification).filter(([, v]) => v),
      meddiccRows: Object.entries(meddicc).filter(([, v]) => v && typeof v === "object"),
    };
  }, [data]);

  if (loading) {
    return (
      <div
        data-testid="shared-loading"
        style={{
          minHeight: "100vh",
          background: BG,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: `2px solid ${ACCENT}33`,
            borderTopColor: ACCENT,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="shared-error"
        style={{
          minHeight: "100vh",
          background: BG,
          color: TEXT_PRIMARY,
          fontFamily: "'DM Sans', sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Link unavailable</h1>
          <p style={{ fontSize: 14, color: TEXT_MUTED, marginBottom: 20 }}>{error}</p>
          <a
            href="/"
            style={{
              display: "inline-block",
              background: ACCENT,
              color: "#fff",
              textDecoration: "none",
              padding: "10px 22px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Go to Inference
          </a>
        </div>
      </div>
    );
  }

  const {
    identity, classification, meddicc, signals, contacts, hypotheses, articles,
    identityRows, classificationRows, meddiccRows,
  } = view;

  return (
    <div
      data-testid="shared-analysis-page"
      style={{
        minHeight: "100vh",
        background: BG,
        color: TEXT_PRIMARY,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
      `}</style>

      {/* Top bar */}
      <div
        style={{
          background: SURFACE_2,
          borderBottom: `1px solid ${BORDER}`,
          padding: "14px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              background: ACCENT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            I
          </div>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Inference</span>
          <Badge label="READ-ONLY SHARED VIEW" color={TEXT_MUTED} />
        </div>
        <a
          href="/"
          data-testid="shared-view-signup-link"
          style={{
            fontSize: 12,
            color: TEXT_MUTED,
            textDecoration: "none",
            border: `1px solid ${BORDER}`,
            padding: "6px 14px",
            borderRadius: 6,
          }}
        >
          Get Inference →
        </a>
      </div>

      {/* Company header */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: fitColor(data.fit_score || 0),
              boxShadow: `0 0 10px ${fitColor(data.fit_score || 0)}77`,
            }}
          />
          <div style={{ flex: 1 }}>
            <h1
              data-testid="shared-company-name"
              style={{
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: "-0.025em",
                marginBottom: 4,
              }}
            >
              {data.company_name || "Untitled Analysis"}
            </h1>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                fontSize: 12,
                color: TEXT_MUTED,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              {data.domain && <span>{data.domain}</span>}
              {classification.vertical && (
                <>
                  <span style={{ color: TEXT_DIM }}>·</span>
                  <span>{classification.vertical}</span>
                </>
              )}
              {data.created_at && (
                <>
                  <span style={{ color: TEXT_DIM }}>·</span>
                  <span>
                    Scanned {new Date(data.created_at).toLocaleDateString()}
                  </span>
                </>
              )}
            </div>
          </div>
          <div
            style={{
              background: SURFACE_2,
              border: `1px solid ${BORDER}`,
              borderRadius: 10,
              padding: "14px 22px",
              textAlign: "center",
              minWidth: 110,
            }}
          >
            <div
              style={{
                fontSize: 9,
                color: TEXT_DIM,
                fontFamily: "'DM Mono', monospace",
                marginBottom: 4,
                letterSpacing: "0.08em",
              }}
            >
              FIT SCORE
            </div>
            <div
              data-testid="shared-fit-score"
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: fitColor(data.fit_score || 0),
                lineHeight: 1,
              }}
            >
              {data.fit_score ?? "—"}
            </div>
          </div>
        </div>

        {/* Sections */}
        {identityRows.length > 0 && (
          <Section title="Company Identity">
            {identityRows
              .map(([k, v]) => (
                <KVRow key={k} k={humanize(k)} v={v} />
              ))}
          </Section>
        )}

        {classificationRows.length > 0 && (
          <Section title="Classification">
            {classificationRows
              .map(([k, v]) => (
                <KVRow key={k} k={humanize(k)} v={v} />
              ))}
          </Section>
        )}

        {signals.length > 0 && (
          <Section title="Signals">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {signals.map((s, i) => (
                <div
                  key={i}
                  style={{
                    background: SURFACE_2,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    padding: "12px 14px",
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                  }}
                >
                  {s.strength && (
                    <Badge
                      label={String(s.strength).toUpperCase()}
                      color={
                        s.strength === "high"
                          ? "#22C55E"
                          : s.strength === "medium"
                          ? "#F59E0B"
                          : TEXT_MUTED
                      }
                    />
                  )}
                  <div style={{ flex: 1, fontSize: 13, color: TEXT_PRIMARY }}>
                    {s.description || s.text || s.title}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {hypotheses.length > 0 && (
          <Section title="Hypotheses">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {hypotheses.map((h, i) => (
                <div
                  key={i}
                  style={{
                    background: SURFACE_2,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    padding: "12px 14px",
                  }}
                >
                  {h.title && (
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        marginBottom: 6,
                        color: TEXT_PRIMARY,
                      }}
                    >
                      {h.title}
                    </div>
                  )}
                  {Array.isArray(h.pain_points) && h.pain_points.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {h.pain_points.map((p, j) => (
                        <li
                          key={j}
                          style={{
                            fontSize: 12,
                            color: TEXT_MUTED,
                            marginBottom: 4,
                          }}
                        >
                          {p}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {meddiccRows.length > 0 && (
          <Section title="MEDDICC">
            {meddiccRows
              .map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    padding: "10px 0",
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: TEXT_DIM,
                      fontFamily: "'DM Mono', monospace",
                      textTransform: "uppercase",
                      marginBottom: 4,
                      letterSpacing: "0.05em",
                    }}
                  >
                    {humanize(k)}
                  </div>
                  <div style={{ fontSize: 13, color: TEXT_PRIMARY }}>
                    {v.value || v.summary || v.notes || humanize(v.status || "unknown")}
                  </div>
                </div>
              ))}
          </Section>
        )}

        {contacts.length > 0 && (
          <Section title="Key Contacts">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {contacts.slice(0, 20).map((c, i) => (
                <div
                  key={i}
                  style={{
                    background: SURFACE_2,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    padding: "10px 14px",
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {c.name || "Unknown"}
                    </div>
                    <div style={{ fontSize: 11, color: TEXT_MUTED }}>
                      {c.title || c.role || ""}
                    </div>
                  </div>
                  {(c.role_type || c.role) && (
                    <Badge label={String(c.role_type || c.role).toUpperCase()} />
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {articles.length > 0 && (
          <Section title="Recent Articles">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {articles.slice(0, 15).map((a, i) => (
                <div
                  key={i}
                  style={{
                    background: SURFACE_2,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    {a.headline || a.title || "Untitled"}
                  </div>
                  {(a.date || a.published || a.publisher) && (
                    <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 4 }}>
                      {[a.publisher, a.date || a.published]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                  {a.relevance && (
                    <div style={{ fontSize: 12, color: TEXT_MUTED }}>
                      {a.relevance}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Footer */}
        <div
          style={{
            textAlign: "center",
            padding: "40px 0 20px",
            color: TEXT_DIM,
            fontSize: 11,
            fontFamily: "'DM Mono', monospace",
          }}
        >
          Shared read-only view · Powered by Inference
        </div>
      </div>
    </div>
  );
}
